import React, { useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { authClient } from "@/lib/auth-client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Link, useSearch } from "@tanstack/react-router"
import { GoogleIcon } from "@/components/icons/GoogleIcon"
import { trackEvent, markPendingSignup } from "@/components/Analytics"

// better-auth returns a plain error object ({ code, message }), NOT an Error
// instance — so `instanceof Error` checks miss it and we'd fall back to a
// useless generic message. Map the codes users actually hit to friendly copy,
// and fall back to better-auth's own message before the generic catch-all.
const SIGN_UP_ERROR_COPY: Record<string, string> = {
  // better-auth's email/password signup returns the _USE_ANOTHER_EMAIL variant;
  // keep both keys so the friendly copy wins regardless of which code surfaces.
  USER_ALREADY_EXISTS: `An account with this email already exists. Try signing in instead.`,
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: `An account with this email already exists. Try signing in instead.`,
  PASSWORD_TOO_SHORT: `Password must be at least 8 characters.`,
  PASSWORD_TOO_LONG: `That password is too long. Try a shorter one.`,
  INVALID_EMAIL: `That doesn't look like a valid email address.`,
}

function signUpErrorMessage(err: {
  code?: string
  message?: string
}): string {
  if (err.code && SIGN_UP_ERROR_COPY[err.code]) return SIGN_UP_ERROR_COPY[err.code]
  if (err.message && err.message.length > 0) return err.message
  return `Something went wrong creating your account. Please try again.`
}

export function SignUpForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<`div`>) {
  const [email, setEmail] = useState(``)
  const [password, setPassword] = useState(``)
  const [repeatPassword, setRepeatPassword] = useState(``)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  // Carried over from /login (e.g. a guide copy interrupted by the login wall:
  // `/guide/<slug>/<id>?copy=1`). Honoring it here keeps the copy funnel alive
  // for brand-new accounts, not just returning users.
  const search = useSearch({ from: `/sign-up` }) as
    | { redirect?: string }
    | undefined

  const resolveRedirect = () => search?.redirect || `/dashboard`

  useEffect(() => {
    setEmail(``)
    setPassword(``)
    setRepeatPassword(``)
    setError(null)
    setIsLoading(false)
    setSuccess(false)
  }, [])

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError(`Password must be at least 8 characters.`)
      return
    }
    if (password !== repeatPassword) {
      setError(`Passwords do not match`)
      return
    }
    // Pairs with GA4's auto `form_start` to make the signup step measurable —
    // the SPA preventDefault means the automatic `form_submit` never fires.
    trackEvent(`form_submit`, { form_name: `sign_up` })
    setIsLoading(true)
    try {
      // callbackURL rides the verification email's link, so when email
      // verification is required the user still lands on the carried redirect
      // (auto-signed-in) after verifying — not stranded on /dashboard.
      const { error: signUpError } = await authClient.signUp.email({
        email,
        password,
        name: email,
        callbackURL: resolveRedirect(),
      })
      if (signUpError) {
        setError(signUpErrorMessage(signUpError))
        return
      }
      // The activation funnel's first conversion — mark `sign_up` as a key event
      // in GA4. Google signups emit this on redirect-return (see markPendingSignup).
      trackEvent(`sign_up`, { method: `email` })
      // With verification required there's no session yet — stay here and show
      // the "check your email" state. Without it (dev) we're signed in now, so
      // go straight to the redirect target.
      const { data: session } = await authClient.getSession()
      if (session) {
        window.location.href = resolveRedirect()
      } else {
        setSuccess(true)
      }
    } catch (err: unknown) {
      // Network/unexpected throws (not better-auth's returned error object).
      setError(err instanceof Error ? err.message : `Something went wrong. Please try again.`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSignUp = async () => {
    setIsLoading(true)
    setError(null)
    // OAuth leaves the page, so we can't fire `sign_up` here — stash the method
    // and let Analytics emit it once we land back authenticated.
    markPendingSignup(`google`)
    try {
      await authClient.signIn.social({ provider: `google`, callbackURL: resolveRedirect() })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : `Google sign-up failed`)
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="text-center py-4">
        <p className="font-semibold mb-1">Check your email</p>
        <p className="text-sm text-muted-foreground">Confirm your account to get started.</p>
      </div>
    )
  }

  return (
    <div className={cn(`flex flex-col gap-6`, className)} {...props}>
      <form onSubmit={handleSignUp}>
        <div className="flex flex-col gap-5">
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="repeat-password">Repeat Password</Label>
            <Input
              id="repeat-password"
              type="password"
              required
              value={repeatPassword}
              onChange={(e) => setRepeatPassword(e.target.value)}
            />
          </div>
          {typeof error === `string` && error.length > 0 && (
            <p className="text-sm text-red-500">{error}</p>
          )}
          <Button type="submit" className="w-full rounded-full h-11" disabled={isLoading}>
            {isLoading ? `Creating account...` : `Sign up`}
          </Button>
        </div>
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">Or</span>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-full h-11"
          onClick={handleGoogleSignUp}
          disabled={isLoading}
        >
          <GoogleIcon className="mr-2 h-4 w-4" />
          Continue with Google
        </Button>
        <div className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{` `}
          <Link
            to="/login"
            search={{ redirect: search?.redirect }}
            className="text-primary font-medium hover:underline underline-offset-4"
          >
            Sign in
          </Link>
        </div>
      </form>
    </div>
  )
}
